import { newId } from "@codesandbox/pitcher-client";

import { mapLanguage } from "./Languages";

// There's a proxy sandbox to work around cors issues
// https://codesandbox.io/p/sandbox/codeium-proxy-scj86v
const CODEIUM_API_ROOT = window.location.href.includes("csb.app")
  ? "https://scj86v-3000.csb.app"
  : "https://server.codeium.com";
const PRIVATE_KEY = "8ecd85da-3af0-11ee-be56-0242ac120002";
// This is to ensure they use our internal api keys
const AUTH_SOURCE = 2;
const IDE_NAME = "codesandbox";
const IDE_VERSION = "1.0.0";
const EXTENSION_VERSION = "1.0.0";

let lastReqId = 0;
const sessionId = newId();

const hashCache = new Map<string, string>();
async function hashString(val: string, maxLength = 24): Promise<string> {
  const cached = hashCache.get(val);
  if (cached) {
    return cached;
  }

  const enc = new TextEncoder();
  const hash = await crypto.subtle.digest("SHA-1", enc.encode(val));
  const combined = Array.from(new Uint8Array(hash))
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")
    .slice(-maxLength);
  hashCache.set(val, combined);
  return combined;
}

interface IRequestMetadata {
  api_key: string;
  auth_source: number;
  ide_name: string;
  ide_version: string;
  extension_version: string;
  request_id: number;
  session_id: string;
}

function getMetadata(uidHash: string): IRequestMetadata {
  const id = lastReqId++;

  return {
    api_key: uidHash,
    auth_source: AUTH_SOURCE,
    ide_name: IDE_NAME,
    ide_version: IDE_VERSION,
    extension_version: EXTENSION_VERSION,
    request_id: id,
    session_id: sessionId,
  };
}

function getAuthHeader(uidHash: string): string {
  return `Basic ${uidHash}-${sessionId}`;
}

export interface ICompletionRequest {
  document: {
    text: string;
    language: number;
    cursor_offset: number;
    line_ending: string;
    editor_language: string;
  };
  editor_options: {
    tab_size: number;
    insert_spaces: boolean;
  };
}

export interface IRawCompletionPosition {
  row: string; // actually a number, i64
  col: string; // actually a number, i64
}

export interface IRawCompletionSuffix {
  text: string;
  deltaCursorOffset: string;
}

export interface IRawCompletionItem {
  completion: {
    completionId: string; // a uuid
    text: string;
    stop: string;
    score: number;
    tokens: string[]; // actually numbers but whatever
    decodedTokens: string[];
    probabilities: number[];
    adjustedProbabilities: number[];
    generatedLength: string; // actually a number
  };
  range: {
    startOffset?: string; // actually a number
    endOffset?: string; // actually a number
    startPosition?: IRawCompletionPosition;
    endPosition?: IRawCompletionPosition;
  };
  source: "COMPLETION_SOURCE_NETWORK";
  suffix?: IRawCompletionSuffix;
}

export interface IRawCompletionResponse {
  state: {
    state: "CODEIUM_STATE_SUCCESS" | string;
    message: string;
  };
  completionItems: IRawCompletionItem[];
}

export interface ICompletionSuffix {
  /**
   * Text to insert after the cursor when accepting the completion.
   */
  text: string;

  /**
   * Cursor position delta (as signed offset) from the end of the inserted
   * completion (including the suffix).
   */
  deltaCursorOffset: number;
}

export interface ICompletionPosition {
  row: number;
  col: number;
}

export interface ICompletionItem {
  completion: {
    completionId: string; // a uuid
    text: string;
    score: number;
    generatedLength: number;
  };
  // TODO: Figure out entire type for this
  range: {
    startOffset: number;
    endOffset: number;
    startPosition?: ICompletionPosition;
    endPosition?: ICompletionPosition;
  };
  suffix?: ICompletionSuffix;
}

export interface ICompletionRequestOpts {
  uid: string;
  document: {
    text: string;
    language: string;
    cursorOffset: number;
    lineEnding: string;
  };
  editorOptions: {
    tabSize: number;
    insertSpaces: boolean;
  };
}

export async function getCompletions(
  opts: ICompletionRequestOpts,
  signal?: AbortSignal | null,
): Promise<ICompletionItem[]> {
  const hashedUid = await hashString(opts.uid);

  await registerUser(hashedUid);

  const req: ICompletionRequest = {
    document: {
      text: opts.document.text,
      language: mapLanguage(opts.document.language),
      cursor_offset: opts.document.cursorOffset,
      line_ending: opts.document.lineEnding,
      editor_language: opts.document.language,
    },
    editor_options: {
      tab_size: opts.editorOptions.tabSize,
      insert_spaces: opts.editorOptions.insertSpaces,
    },
  };

  const res = await fetch(
    `${CODEIUM_API_ROOT}/exa.language_server_pb.LanguageServerService/GetCompletions`,
    {
      method: "POST",
      body: JSON.stringify({
        ...req,
        metadata: getMetadata(hashedUid),
      }),
      headers: {
        Authorization: getAuthHeader(hashedUid),
        "Content-Type": "application/json",
      },
      signal,
    },
  );
  const json: IRawCompletionResponse = await res.json();
  const completions: ICompletionItem[] =
    json.completionItems?.map((c) => {
      return {
        completion: {
          completionId: c.completion.completionId,
          text: c.completion.text,
          score: c.completion.score,
          generatedLength: +c.completion.generatedLength,
        },
        range: {
          startOffset: Number(c.range.startOffset),
          endOffset: Number(c.range.endOffset),
          startPosition: c.range.startPosition
            ? {
                row: Number(c.range.startPosition.row),
                col: Number(c.range.startPosition.col),
              }
            : undefined,
          endPosition: c.range.endPosition
            ? {
                row: Number(c.range.endPosition.row),
                col: Number(c.range.endPosition.col),
              }
            : undefined,
        },
        suffix: c.suffix
          ? {
              text: c.suffix.text,
              deltaCursorOffset: Number(c.suffix.deltaCursorOffset),
            }
          : undefined,
      };
    }) ?? [];
  return completions;
}

export async function acceptCompletion(
  uid: string,
  completion_id: string,
): Promise<void> {
  const hashedUid = await hashString(uid);
  const res = await fetch(
    `${CODEIUM_API_ROOT}/exa.language_server_pb.LanguageServerService/AcceptCompletion`,
    {
      method: "POST",
      body: JSON.stringify({
        metadata: getMetadata(hashedUid),
        completion_id,
      }),
      headers: {
        Authorization: getAuthHeader(hashedUid),
        "Content-Type": "application/json",
      },
    },
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Request failed: ${res.status} - ${txt}`);
  }
}

let registrationPromise: Promise<void> | null = null;
let isRegistered = false;
async function _registerUser(uidHash: string): Promise<void> {
  if (isRegistered) return;

  const res = await fetch(
    `${CODEIUM_API_ROOT}/exa.api_server_pb.ApiServerService/RegisterExternalUser`,
    {
      method: "POST",
      body: JSON.stringify({
        uid: uidHash,
        auth_source: AUTH_SOURCE,
        private_key: PRIVATE_KEY,
      }),
      headers: {
        Authorization: getAuthHeader(uidHash),
        "Content-Type": "application/json",
      },
    },
  );
  if (!res.ok) {
    const txt = await res.text();
    // Error handling isn't very great for codeium, so if it's not this error (which likely might change?)
    // We log it as a warning
    if (!txt.toLowerCase().includes("user already exists")) {
      // eslint-disable-next-line
      console.warn(`Codeium request failed: ${res.status} - ${txt}`);
    }
  }
  isRegistered = true;
}

function registerUser(uidHash: string) {
  if (isRegistered) {
    return Promise.resolve();
  }

  if (registrationPromise) {
    return registrationPromise;
  } else {
    registrationPromise = _registerUser(uidHash)
      .then(() => {
        isRegistered = true;
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.error(err);

        setTimeout(() => {
          registrationPromise = null;
        }, 2500);
      });
  }
}
